import React from 'react';
import { Modal, Button, Table, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';

const InvoiceDetailModal = ({ show, onHide, invoice, onEmail }) => {

    if (!invoice) return null;

    const handlePrint = () => {
        window.print();
    };

    const handleSendEmail = () => {
        if (onEmail) {
            onEmail(invoice._id);
        }
    };

    // DETERMINE INVOICE TYPE (Plan vs Consultation)
    // "Consultation Fee" (and variants like "Online Consultation Fee", "Rescheduling Fee") is for Patients.
    // Anything NOT containing "Consultation" is a System/Subscription Plan.
    const isPlanInvoice = invoice.subscriptionPlan && !invoice.subscriptionPlan.toLowerCase().includes('consultation');

    // Helper for Dates
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Calculate Entitlement Dates (Mocking logic based on Monthly assumption if not present)
    const startDate = new Date(invoice.paidAt || invoice.createdAt);
    const expiryDate = new Date(startDate);
    expiryDate.setDate(expiryDate.getDate() + 30); // Default 30 days
    const duration = "1 Month (30 Days)";

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="invoice-modal" enforceFocus={false}>
            {/* Header hidden in print, simple close button */}
            <Modal.Header closeButton className="border-bottom-0 bg-white pt-4 px-4 pb-0 d-print-none text-end w-100 justify-content-end">
                {/* No Title Here */}
            </Modal.Header>
            <Modal.Body className="p-0 bg-white rounded-3 overflow-hidden">

                {/* -------------------------------------------------------------------------- */}
                {/* MODE A: SYSTEM / SUBSCRIPTION PLAN INVOICE (Super Admin / Clinic Admin View) */}
                {/* -------------------------------------------------------------------------- */}
                {isPlanInvoice ? (
                    <div className="invoice-container p-5">

                        {/* 1. BRANDING & STATUS HEADER */}
                        <div className="d-flex justify-content-between align-items-start mb-5 border-bottom pb-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-primary-600 text-white p-3 rounded-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '64px', height: '64px' }}>
                                    <Icon icon="solar:health-bold" className="display-6" />
                                </div>
                                <div>
                                    <h3 className="fw-bolder text-primary-dark mb-0 ls-1">MEDORO Health</h3>
                                    <p className="text-secondary text-sm mb-0 fw-bold text-uppercase tracking-widest">Subscription Invoice</p>
                                </div>
                            </div>
                            <div className="text-end">
                                <h2 className="display-6 fw-bolder text-dark mb-1">INVOICE</h2>
                                <h5 className="fw-bold mb-2 text-secondary">#{invoice.invoiceNumber}</h5>
                                <Badge bg={invoice.status === 'Paid' ? 'success' : 'warning'} className="px-4 py-2 rounded-pill text-uppercase text-xs fw-bolder tracking-wider shadow-sm">
                                    {invoice.status}
                                </Badge>
                            </div>
                        </div>

                        {/* 2. DETAILS GRID - BILLED TO & PAYMENT INFO */}
                        <div className="row g-5 mb-5 align-items-start">
                            {/* Left: Billed To */}
                            <div className="col-sm-6">
                                <div className="p-4 bg-light rounded-4 h-100 border border-light-subtle">
                                    <h6 className="text-uppercase text-secondary text-xs fw-bolder mb-3 d-flex align-items-center gap-2">
                                        <Icon icon="solar:user-bold" /> Billed To:
                                    </h6>
                                    <h4 className="fw-bold text-dark mb-2">{invoice.clinicId?.name || 'Valued Partner'}</h4>

                                    <div className="d-flex flex-column gap-1 text-sm text-dark fw-bold">
                                        <span className="d-flex align-items-center gap-2"><Icon icon="solar:map-point-linear" /> {invoice.clinicId?.address || 'Address on file'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Payment & Plan Meta */}
                            <div className="col-sm-6">
                                <div className="p-4 bg-light rounded-4 h-100 border border-light-subtle">
                                    <h6 className="text-uppercase text-secondary text-xs fw-bolder mb-3 d-flex align-items-center gap-2">
                                        <Icon icon="solar:bill-list-bold" /> Payment Details:
                                    </h6>
                                    <div className="d-flex flex-column gap-3">
                                        <div className="d-flex justify-content-between border-bottom border-light-subtle pb-2">
                                            <span className="text-secondary text-sm fw-medium">Order ID</span>
                                            <span className="fw-bold text-dark font-monospace">{invoice.razorpayOrderId || 'N/A'}</span>
                                        </div>
                                        <div className="d-flex justify-content-between border-bottom border-light-subtle pb-2">
                                            <span className="text-secondary text-sm fw-medium">Transaction ID</span>
                                            <span className="fw-bold text-dark font-monospace">{invoice.razorpayPaymentId || 'N/A'}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-secondary text-sm fw-medium">Payment Date</span>
                                            <span className="fw-bold text-dark">{formatDate(invoice.paidAt || invoice.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 3. PLAN DESCRIPTION BLOCK - ENHANCED */}
                        <div className="mb-5">
                            <h6 className="text-uppercase text-secondary text-xs fw-bolder mb-3 ps-1">Subscription Plan Details</h6>
                            <div className="border rounded-4 overflow-hidden shadow-sm">
                                <Table className="mb-0 align-middle">
                                    <thead className="bg-base border-bottom">
                                        <tr>
                                            <th className="py-3 px-4 text-xs text-uppercase fw-bolder text-secondary">Plan Description</th>
                                            <th className="py-3 px-4 text-xs text-uppercase fw-bolder text-secondary text-center">Start Date</th>
                                            <th className="py-3 px-4 text-xs text-uppercase fw-bolder text-secondary text-center">Expiry Date</th>
                                            <th className="py-3 px-4 text-xs text-uppercase fw-bolder text-secondary text-center">Duration</th>
                                            <th className="py-3 px-4 text-xs text-uppercase fw-bolder text-secondary text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        <tr style={{ height: '80px' }}>
                                            <td className="px-4">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="w-48-px h-48-px rounded-3 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center shadow-sm">
                                                        <Icon icon="solar:crown-star-bold" className="text-2xl" />
                                                    </div>
                                                    <div>
                                                        <h6 className="fw-bold text-dark mb-0">{invoice.subscriptionPlan} Plan</h6>
                                                        <span className="text-xs text-secondary fw-medium bg-secondary-50 px-2 py-1 rounded">Monthly Recurring</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 text-center text-sm fw-medium text-dark">{formatDate(startDate)}</td>
                                            <td className="px-4 text-center text-sm fw-medium text-dark">{formatDate(expiryDate)}</td>
                                            <td className="px-4 text-center text-sm fw-medium text-dark">{duration}</td>
                                            <td className="px-4 text-end fw-bold text-dark h6 mb-0">{invoice.currency} {Number(invoice.amount).toLocaleString()}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </div>

                        {/* 4. TOTALS ROW - FULL WIDTH DESIGN */}
                        <div className="mb-5">
                            <div className="bg-primary-50 rounded-4 p-0 overflow-hidden border border-primary-100">
                                <div className="row g-0">
                                    {/* Left Spacer / Note */}
                                    <div className="col-md-7 p-4 border-end border-primary-100 d-none d-md-block">
                                        <p className="text-xs text-primary-800 fw-bold text-uppercase mb-2">Terms & Conditions</p>
                                        <p className="text-xs text-secondary mb-0" style={{ maxWidth: '90%' }}>
                                            Payment for the Medoro Health platform subscription is non-refundable.
                                            Access is granted for the duration specified above.
                                            For support, verify payment via Transaction ID.
                                        </p>
                                    </div>

                                    {/* Right Totals */}
                                    <div className="col-md-5 p-4 bg-white">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-secondary fw-medium text-sm">Subtotal</span>
                                            <span className="fw-bold text-dark">{invoice.currency} {Number(invoice.amount).toLocaleString()}</span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-secondary fw-medium text-sm">Tax (0%)</span>
                                            <span className="fw-bold text-dark">{invoice.currency} 0.00</span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top border-dashed">
                                            <span className="text-dark fw-bolder h5 mb-0">Total Paid</span>
                                            <span className="text-primary fw-bolder h4 mb-0">{invoice.currency} {Number(invoice.amount).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-auto border-top pt-4">
                            <p className="mb-1 text-secondary text-sm fw-bold">Thank you for choosing Medoro Health!</p>
                            <p className="mb-0 text-secondary text-xs opacity-75">Computer-generated invoice. No signature required.</p>
                        </div>
                    </div>
                ) : (
                    /* -------------------------------------------------------------------------- */
                    /* MODE B: PATIENT CONSULTATION INVOICE                                     */
                    /* -------------------------------------------------------------------------- */
                    <div className="p-5">
                        <div className="d-flex justify-content-between align-items-start mb-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-light p-3 rounded-circle d-flex align-items-center justify-content-center border" style={{ width: '56px', height: '56px' }}>
                                    <Icon icon="solar:hospital-bold" className="text-secondary display-6" />
                                </div>
                                <div>
                                    <h4 className="fw-bold text-dark mb-0">{invoice.clinicId?.name || 'Clinic Invoice'}</h4>
                                    <p className="text-secondary mb-0 text-sm">{invoice.clinicId?.address}</p>
                                </div>
                            </div>
                            <div className="text-end">
                                <div className={`badge mb-2 px-3 py-2 rounded-pill ${invoice.status === 'Paid' ? 'bg-success-subtle text-success border border-success' : 'bg-warning-subtle text-warning border border-warning'}`}>
                                    {invoice.status.toUpperCase()}
                                </div>
                                <p className="text-secondary text-sm mb-0 fw-medium">Date: {formatDate(invoice.issueDate || invoice.createdAt)}</p>
                            </div>
                        </div>

                        <div className="p-4 bg-light rounded-4 mb-4 border border-light-subtle">
                            <Row>
                                <Col sm={6}>
                                    <h6 className="text-uppercase text-secondary text-xs fw-bolder tracking-wider mb-3">Bill To Patient:</h6>
                                    <p className="fw-bold text-dark mb-1 h5">{invoice.patientId?.name || invoice.customerInfo?.name || 'Guest Patient'}</p>
                                    <p className="text-secondary text-sm mb-1">{invoice.patientId?.email || invoice.customerInfo?.email}</p>
                                    <p className="text-secondary text-sm mb-0">{invoice.patientId?.phone || invoice.customerInfo?.phone}</p>
                                </Col>
                                <Col sm={6} className="text-sm-end mt-3 mt-sm-0">
                                    <h6 className="text-uppercase text-secondary text-xs fw-bolder tracking-wider mb-3">Invoice Details:</h6>
                                    <p className="text-dark fw-bold mb-1">#{invoice.invoiceNumber}</p>
                                </Col>
                            </Row>
                        </div>

                        <div className="table-responsive rounded-3 border mb-4">
                            <Table className="mb-0">
                                <thead className="bg-secondary-50">
                                    <tr>
                                        <th className="py-3 px-4 text-secondary text-xs text-uppercase fw-bold">Item / Service</th>
                                        <th className="py-3 px-4 text-end text-secondary text-xs text-uppercase fw-bold">Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.items && invoice.items.length > 0 ? (
                                        invoice.items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="px-4 py-3 fw-medium text-dark">{item.description || 'Consultation Service'}</td>
                                                <td className="px-4 py-3 text-end fw-bold">{invoice.currency} {Number(item.total || item.amount || invoice.amount).toLocaleString()}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td className="px-4 py-3 fw-medium text-dark">Medical Consultation Fee</td>
                                            <td className="px-4 py-3 text-end fw-bold">{invoice.currency} {Number(invoice.amount).toLocaleString()}</td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot className="border-top bg-white">
                                    <tr>
                                        <td className="px-4 py-4 fw-bold text-dark h5 text-end border-0">Total Amount</td>
                                        <td className="px-4 py-4 fw-bold text-primary h5 text-end border-0">{invoice.currency} {Number(invoice.amount).toLocaleString()}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </div>
                )}

            </Modal.Body>
            <Modal.Footer className="border-top-0 bg-white p-4 justify-content-center d-print-none">
                <Button variant="light" onClick={onHide} className="px-4 fw-bold rounded-3">Close</Button>
                <Button variant="outline-dark" onClick={handlePrint} className="d-flex align-items-center gap-2 px-4 fw-bold rounded-3">
                    <Icon icon="solar:printer-bold" /> Print / Download
                </Button>
                <Button variant="primary" onClick={handleSendEmail} className="d-flex align-items-center gap-2 px-4 fw-bold rounded-3 shadow-sm icon-link-hover">
                    <Icon icon="solar:letter-bold" /> Email Invoice
                </Button>
            </Modal.Footer>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .invoice-modal .modal-content, .invoice-modal .modal-content * { visibility: visible; }
                    .invoice-modal .modal-content { position: absolute; left: 0; top: 0; width: 100%; border: none; box-shadow: none; padding: 0 !important; }
                    .modal-footer, .btn-close { display: none !important; }
                    .modal-body { padding: 0 !important; background: white !important; -webkit-print-color-adjust: exact; }
                    .bg-primary-50 { background-color: #f0f9ff !important; }
                    .bg-primary-600 { background-color: #0284c7 !important; color: white !important; }
                    .badge { border: 1px solid #ddd; color: black !important; }
                }
            `}</style>
        </Modal>
    );
};

export default InvoiceDetailModal;
